/** Browser transport for origin-resolved public endpoints only.
 *
 * This module has no Supabase import and cannot attach identity or X-Tenant.
 * The isolated landing build imports this seam without pulling member auth into
 * the audit artifact.
 */
const apiOrigin = import.meta.env.VITE_API_URL?.trim().replace(/\/+$/, "") ?? "";
const API_BASE = apiOrigin.endsWith("/api/lms") ? apiOrigin : `${apiOrigin}/api/lms`;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /**
     * The parsed `detail` body, when the server sent a structured one.
     *
     * A flattened message is enough for a toast, but not for every refusal: the
     * session portal's 409 carries the newly effective destination beside its
     * sentence, and the design requires showing that value next to the
     * instructor's unsaved proposal rather than telling them they lost and
     * discarding what they typed. Callers that do not need it ignore it, and
     * `message` is unchanged for them.
     */
    public detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Read a FastAPI 422 body into one readable sentence.
 *
 * Pydantic prefixes a custom validator's message with "Value error, ", which is
 * framework noise to a user reading a toast. The field name is kept only when
 * more than one field failed, since a single-field form has already made the
 * subject obvious.
 */
function validationMessage(detail: unknown): string | null {
  if (!Array.isArray(detail) || detail.length === 0) return null;
  const messages: string[] = [];
  for (const item of detail) {
    if (typeof item !== "object" || item === null) continue;
    const entry = item as { msg?: unknown; loc?: unknown };
    if (typeof entry.msg !== "string") continue;
    const message = entry.msg.replace(/^Value error,\s*/, "");
    const field = Array.isArray(entry.loc)
      ? entry.loc.filter((part) => typeof part === "string" && part !== "body").at(-1)
      : undefined;
    messages.push(
      detail.length > 1 && typeof field === "string" ? `${field}: ${message}` : message,
    );
  }
  return messages.length > 0 ? messages.join(". ") : null;
}

function structuredMessage(detail: unknown): string | null {
  if (typeof detail !== "object" || detail === null || Array.isArray(detail)) return null;
  const message = (detail as { message?: unknown }).message;
  return typeof message === "string" && message.trim() ? message : null;
}

export async function httpFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let detail = res.statusText;
    let payload: unknown;
    try {
      const body = await res.json();
      payload = body?.detail;
      if (typeof body?.detail === "string") {
        detail = body.detail;
      } else {
        // FastAPI returns `detail` as an ARRAY for a 422, so the string check
        // above misses every validation failure and the user gets the bare
        // status text ("Unprocessable Content") instead of the reason. That is
        // worst exactly where the message matters most: a field-level rule that
        // exists to teach, like refusing a pasted secret as a credential name.
        const validation = validationMessage(body?.detail);
        const structured = structuredMessage(body?.detail);
        if (validation) detail = validation;
        else if (structured) detail = structured;
      }
    } catch {
      // Keep the status text when the response body is not JSON.
    }
    throw new ApiError(res.status, detail, payload);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function publicFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.delete("Authorization");
  headers.delete("X-Tenant");
  return httpFetch<T>(path, { ...init, headers, credentials: "omit" });
}

export const publicApi = {
  get: <T>(path: string) => publicFetch<T>(path),
  post: <T>(path: string, body?: unknown) =>
    publicFetch<T>(path, {
      method: "POST",
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
};
